// Shared body of the "How to Make a Claim" guide. Rendered inside the
// Help & Support modal (/settings/help?guide=claims) and on the public
// /claims-guide page, so both always show the same content.
export const ClaimsGuideContent = (): JSX.Element => {
  return (
    <div className="space-y-6">
      <p
        className="text-sm leading-relaxed"
        style={{ color: "var(--textColorMuted)" }}
      >
        If a parcel is lost or arrives damaged, you can submit a claim in
        Connexx in a couple of minutes. Watch the video below, or follow the
        steps.
      </p>

      <div>
        <h3
          className="text-lg font-semibold mb-3"
          style={{ color: "var(--textColorPrimary)" }}
        >
          Watch video tutorial
        </h3>
        <div
          className="relative rounded-lg overflow-hidden"
          style={{
            width: "100%",
            backgroundColor: "var(--backgroundColor)",
          }}
        >
          <video
            controls
            playsInline
            preload="metadata"
            className="w-full rounded-lg"
            style={{ maxHeight: "60vh" }}
          >
            <source
              src="/documentation/how-to-make-a-claim.mp4"
              type="video/mp4"
            />
            Your browser does not support video playback.
          </video>
        </div>
      </div>

      <div>
        <h3
          className="text-lg font-semibold mb-3"
          style={{ color: "var(--textColorPrimary)" }}
        >
          Step by step
        </h3>
        <ol
          className="list-decimal pl-5 space-y-6 text-sm leading-relaxed"
          style={{ color: "var(--textColorPrimary)" }}
        >
          <li>
            <span className="font-semibold">
              Select a shipment and click on &quot;Get Support&quot;
            </span>
            <div
              className="mt-3 rounded-lg border overflow-hidden max-w-xl"
              style={{ borderColor: "var(--borderColor)" }}
            >
              <img
                src="/documentation/claims-guide/step-1.jpg"
                alt="Shipment panel with the Get support button highlighted"
                loading="lazy"
                className="w-full"
              />
            </div>
          </li>
          <li>
            <span className="font-semibold">Choose Lost or Damaged</span>
            <div
              className="mt-3 rounded-lg border overflow-hidden max-w-md"
              style={{ borderColor: "var(--borderColor)" }}
            >
              <img
                src="/documentation/claims-guide/step-2.jpg"
                alt="Choosing Lost or Damaged when creating a support case"
                loading="lazy"
                className="w-full"
              />
            </div>
          </li>
          <li>
            <span className="font-semibold">Confirm eligibility</span>
            <p className="mt-1" style={{ color: "var(--textColorMuted)" }}>
              Connexx checks the parcel's tracking history and confirms your
              claim can go ahead.
            </p>
            <div
              className="mt-3 rounded-lg border overflow-hidden max-w-md"
              style={{ borderColor: "var(--borderColor)" }}
            >
              <img
                src="/documentation/claims-guide/step-3.jpg"
                alt="Eligibility checks and tracking history for the parcel"
                loading="lazy"
                className="w-full"
              />
            </div>
          </li>
          <li>
            <span className="font-semibold">Fill in the details</span>
            <p className="mt-1" style={{ color: "var(--textColorMuted)" }}>
              Describe the goods and packaging, enter your claim value, and
              upload supporting evidence such as proof of value.
            </p>
            <div
              className="mt-3 rounded-lg border overflow-hidden max-w-md"
              style={{ borderColor: "var(--borderColor)" }}
            >
              <img
                src="/documentation/claims-guide/step-4.jpg"
                alt="Evidence form with goods, claim value, and document uploads"
                loading="lazy"
                className="w-full"
              />
            </div>
          </li>
          <li>
            <span className="font-semibold">Review and submit</span>
            <p className="mt-1" style={{ color: "var(--textColorMuted)" }}>
              Check the details and click Submit request. You can also save a
              draft and finish later.
            </p>
            <div
              className="mt-3 rounded-lg border overflow-hidden max-w-md"
              style={{ borderColor: "var(--borderColor)" }}
            >
              <img
                src="/documentation/claims-guide/step-5.jpg"
                alt="Review screen with claim details before submitting"
                loading="lazy"
                className="w-full"
              />
            </div>
          </li>
          <li>
            <span className="font-semibold">Track your claim</span>
            <p className="mt-1" style={{ color: "var(--textColorMuted)" }}>
              Follow your claim on the Support Cases page. We will email you
              updates, and you can reply to those emails to message the team.
            </p>
            <div
              className="mt-3 rounded-lg border overflow-hidden max-w-xl"
              style={{ borderColor: "var(--borderColor)" }}
            >
              <img
                src="/documentation/claims-guide/step-6.png"
                alt="Support Cases page showing the claim and its status"
                loading="lazy"
                className="w-full"
              />
            </div>
          </li>
        </ol>
      </div>
    </div>
  );
};
